import { Component, OnInit, Input } from '@angular/core';

import { Airport, AirportType } from '../models/airport.model';
import { AirportComment, CommentType } from '../models/airport-comment.model';
import { LandingSurfaceType } from '../models/landing-surface.model';
import { PilotControlledLighting } from '../models/pilot-controlled-lighting.model';
import { PatternAltitude } from '../models/pattern-altitude.model';
import { Source } from '../../shared/models/source.model';
import { getSubTags, hasTag } from '../../util/tag-utils';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { MatDialog, MatDialogConfig, MatSnackBar } from '@angular/material';
import { EditPatternAltitudeComponent } from '../edit-pattern-altitude/edit-pattern-altitude.component';
import { EditPilotControlledLightingComponent } from '../edit-pilot-controlled-lighting/edit-pilot-controlled-lighting.component';
import { AirportService } from '../airport.service';
import { EditTagsComponent } from '../../shared/edit-tags/edit-tags.component';
import { DeletePatternAltitudeComponent } from '../delete-pattern-altitude/delete-pattern-altitude.component';
import { DeletePilotControlledLightingComponent } from '../delete-pilot-controlled-lighting/delete-pilot-controlled-lighting.component';


@Component({
  selector: 'app-operations',
  templateUrl: './operations.component.html',
  styleUrls: ['./operations.component.scss']
})


export class OperationsComponent implements OnInit {
  @Input() airport: Airport;
  getSubTags = getSubTags;
  Source = Source;
  editModeSet: boolean;

  constructor( private _toggleService: EditToggleService,
               public _snackBar: MatSnackBar,
               private _editPatternAltitudeDialog: MatDialog,
               private _deletePatternAltitudeDialog: MatDialog,
               private _editPCLDialog: MatDialog,
               private _deletePCLDialog: MatDialog,
               private _airportService: AirportService,
               private _editSpecialOpsDialog: MatDialog
  ) {
  }

  ngOnInit() {
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
  }

  public lightingIntensity(intensity: any): string {
    return PilotControlledLighting.formatLightingIntensity(intensity);
  }

  public formatAircraftClass(aircraftClass: any): string {
    return PatternAltitude.formatAircraftClass(aircraftClass);
  }

  get lightingRemarks(): Array<AirportComment> {
    return this.airport.airportComments
      .filter(comment => comment.commentType === CommentType.Lighting)
      .sort((n1, n2) => n1.sourceId - n2.sourceId);
  }

  get patternAltRemarks(): Array<AirportComment> {
    return this.airport.airportComments
      .filter(comment => comment.commentType === CommentType.PatternAlt)
      .sort((n1, n2) => n1.sourceId - n2.sourceId);
  }

  get pilotControlledLighting(): Array<PilotControlledLighting> {
    return this.airport.pilotControlledLighting
      .sort((n1, n2) => n1.intensity - n2.intensity);
  }

  get patternAltitudes(): Array<PatternAltitude> {
    return this.airport.patternAltitudes
      .sort((n1, n2) => n1.altitude - n2.altitude);
  }

  get windIndicator(): string {
    if (this.airport.windIndicator && this.airport.windIndicatorLighted) {
      return 'Lighted';
    }
    return this.airport.windIndicator ? 'Yes' : 'No';
  }

  get segmentedCircle(): string {
    if (this.airport.segmentedCircle && this.airport.segmentedCircleLighted) {
      return 'Lighted';
    }
    return this.airport.segmentedCircle ? 'Yes' : 'No';
  }

  get beacon(): string {
    let beaconInfo: string;
    if (this.airport.beacon) {
      beaconInfo = 'Present';
      if (this.airport.beaconColor != null && this.airport.beaconColor.length > 0) {
        beaconInfo = beaconInfo + ' (' + this.airport.beaconColor + ')';
      }
      return beaconInfo;
    }
    return 'Not Present';
  }

  async reloadAirport(identifier: string) {
    this.airport = await this._airportService.getAirport(identifier, true);
  }

  get airportSpecialOps(): string {
    const specOps: Array<string> = [];
    /* if (hasTag(this.airport.airportTags, 'Operations::Glider Ops')) {
      specOps.push('Glider Ops');
    }
    if (this.airport.type !== AirportType.Heliport && (hasTag(this.airport.airportTags, 'Operations::Helicopter Ops')
        || this.airport.hasLandingSurfaceType(LandingSurfaceType.Helipad))) {
      specOps.push('Helicopter Ops');
    }
    if (this.airport.type !== AirportType.SeaplaneBase &&
      this.airport.hasLandingSurfaceType(LandingSurfaceType.Waterway)) {
      specOps.push('Seaplane Ops');
    }
    if (hasTag(this.airport.airportTags, 'Operations::Ultralight Ops')) {
      specOps.push('Ultralight Ops');
    }
    if (specOps.length === 0) {
      return 'None reported';
    } */
    if (this.airport.type !== AirportType.Heliport && this.airport.hasLandingSurfaceType(LandingSurfaceType.Helipad)) {
      specOps.push('Helicopter Ops');
    }
    if (this.airport.type !== AirportType.SeaplaneBase && this.airport.hasLandingSurfaceType(LandingSurfaceType.Waterway)) {
      specOps.push('Seaplane Ops');
    }
    if (specOps.length === 0) {
      return '';
    }
    return specOps.join(', ');
  }

  get airportSpecialOpsAirportManager(): string {
    const result: String[] = [];
    for (const currentTag of this.airport.airportTags) {
      if (currentTag.tagName.startsWith('Operations::')) {
        if (currentTag.tagName !== 'Operations::Glider Ops' && currentTag.tagName !== 'Operations::Helicopter Ops'
          && currentTag.tagName !== 'Operations::Ultralight Ops' && currentTag.tagName !== 'Operations::Commercial Ops') {
          result.push(currentTag.displayName);
        }
      }
    }
    return result.sort().join(', ');
  }

  openEditPatternAltDialog(index: number) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.data = {
      airportId: this.airport.airportId,
      patternAltId: null,
      isNew: false,
      patternAltitudes: this.airport.patternAltitudes
    };
    index == null ? dialogConfig.data.isNew = true : dialogConfig.data.patternAlt = this.airport.patternAltitudes[index];
    dialogConfig.data['airID'] = this.airport.airportId;
    dialogConfig.width = '60%';
    const dialogRef = this._editPatternAltitudeDialog.open(EditPatternAltitudeComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        if (isUpdated.isNew) {
          this.airport.patternAltitudes.push(isUpdated.patternAlt);
          this._snackBar.open('Pattern Altitude', 'Added', {
            duration: 2000
          });
        } else {
          this._snackBar.open('Pattern Altitude', 'Updated', {
            duration: 2000
          });
        }
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  deletePatternAlt(index: number) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      airportId: this.airport.airportId,
      patternAltId: this.airport.patternAltitudes[index].patternAltId
    };
    const dialogRef = this._deletePatternAltitudeDialog.open(DeletePatternAltitudeComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this._snackBar.open('Pattern Altitude', 'Deleted', {
          duration: 2000
        });
        this.airport.patternAltitudes.splice(index, 1);
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  openEditPCLDialog(index: number) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.data = {
      airID: this.airport.airportId,
      pclId: null,
      isNew: false,
      PCL: this.airport.pilotControlledLighting
    };
    index == null ? dialogConfig.data.isNew = true : dialogConfig.data.pcl = this.airport.pilotControlledLighting[index];
    dialogConfig.data['airID'] = this.airport.airportId;
    const dialogRef = this._editPCLDialog.open(EditPilotControlledLightingComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        if (isUpdated.isNew) {
          // this.airport.pilotControlledLighting.push(isUpdated.pcl);
          this.reloadAirport(this.airport.identifier);
          this._snackBar.open('Pilot Activated Lighting', 'Added', {
            duration: 2000
          });
        } else {
          this._snackBar.open('Pilot Activated Lighting', 'Updated', {
            duration: 2000
          });
        }
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  deletePCL(index: number) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      airportId: this.airport.airportId,
      pclId: this.airport.pilotControlledLighting[index].pclId
    };
    const dialogRef = this._deletePCLDialog.open(DeletePilotControlledLightingComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this._snackBar.open('Pilot Controlled Lighting', 'Deleted', {
          duration: 2000
        });
        this.airport.pilotControlledLighting.splice(index, 1);
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  openEditTagsDialog(group: String[], except: String[], title: string): void {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      business: this.airport,
      tagsGroup: group,
      except: except,
      title: title
    };

    dialogConfig.width = '100%';
    const dialogRef = this._editSpecialOpsDialog.open(EditTagsComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isSuccess) => {
      if (isSuccess) {
        // this.loadAllTags();
        this.reloadAirport(this.airport.identifier);
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }
}
